import { useEffect } from "react";
import { Link, data } from "react-router";
import { marked } from "marked";
import type { Route } from "./+types/article";
import Shell from "../components/Shell";
import NewsletterSignup from "../components/NewsletterSignup";
import { getArticle, recordView, coverUrl } from "../lib/api";
import { faDate } from "../lib/format";
import { isScheduled } from "../lib/scheduled";
import { OG_IMAGE } from "../lib/faData";
import { currentSource } from "../lib/attribution";
import { analytics } from "../lib/analytics";

export function meta({ data: loaded }: Route.MetaArgs) {
  if (!loaded?.article) return [{ title: "نوشته پیدا نشد | گازار" }];
  const { article } = loaded;
  // The article's own cover, falling back to the shared hero. Until 22 Aug 2026
  // every Farsi page shared one English OG_IMAGE, so every Telegram and
  // WhatsApp card for every article looked identical. Telegram is this
  // surface's main rail, so that card IS the article to most people who see it.
  const share = coverUrl(article.image) ?? OG_IMAGE;
  return [
    { title: `${article.title} | گازار` },
    { name: "description", content: article.description ?? "" },
    { property: "og:title", content: article.title },
    { property: "og:type", content: "article" },
    { property: "og:url", content: `https://fa.gazar.dev/maghale/${article.slug}` },
    { property: "og:description", content: article.description ?? "" },
    { property: "og:image", content: share },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: share },
    { tagName: "link", rel: "canonical", href: `https://fa.gazar.dev/maghale/${article.slug}` },
    // Scheduled: reachable by URL before its date (link previews need the OG
    // tags above), but not to be indexed until it is actually out.
    ...(isScheduled(article.publishedAt) ? [{ name: "robots", content: "noindex" }] : []),
  ];
}

export async function loader({ params }: Route.LoaderArgs) {
  try {
    const article = await getArticle(params.slug);
    // Rendered on the SERVER. The body is markdown authored in admin, and
    // shipping a markdown parser to the browser to re-render text that never
    // changes would cost every reader a download for nothing.
    const html = await marked.parse(article.content ?? article.body);
    return { article, html };
  } catch {
    throw data({ message: "not found" }, { status: 404 });
  }
}

export default function ArticlePage({ loaderData }: Route.ComponentProps) {
  const { article, html } = loaderData;

  // Count the read, once per article per mount.
  //
  // This app shipped without it on 21 Aug 2026, so the one published Farsi
  // article sat at viewCount 0 and that 0 meant "never instrumented" rather
  // than "never read". Two different numbers that look identical is exactly the
  // failure the 30 Oct gate cannot survive. A scheduled article is not out yet,
  // so an early reader is not a read.
  const scheduled = isScheduled(article.publishedAt);
  useEffect(() => {
    if (scheduled) return;
    recordView(article.id);
    analytics.articleRead({ slug: article.slug, src: currentSource() });
  }, [article.id, article.slug, scheduled]);

  return (
    <Shell>
      <div className="bg-white">
      <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8">
        <h1 className="mb-3 text-3xl font-bold leading-[1.7] text-brand-navy md:text-4xl">
          {article.title}
        </h1>
        <time className="mb-8 block text-sm text-ink-muted">{faDate(article.publishedAt)}</time>

        {/* Decorative: the cover repeats what the title already says, so it gets
            an empty alt rather than a description a screen reader has to sit
            through before reaching the article. */}
        {coverUrl(article.image) ? (
          <img
            src={coverUrl(article.image) as string}
            alt=""
            width={1200}
            height={675}
            className="mb-10 aspect-video w-full rounded-smooth-lg border border-border-subtle object-cover"
          />
        ) : null}

        {/* The body is authored by the site owner in admin, so this is first-party
            content, not user input. It is still server-rendered rather than
            passed through a client-side sanitiser, which would run on every
            reader's machine for content only one person can write. */}
        <div className="prose-fa" dangerouslySetInnerHTML={{ __html: html }} />

        {/* Placed BELOW the article and ABOVE the related list: a reader who
            was persuaded should be asked before being offered somewhere else to
            go. This app shipped on 21 Aug with no capture on this route at all. */}
        <NewsletterSignup variant="article" />

        {article.related?.length ? (
          <section className="mt-14 border-t border-border-subtle pt-8">
            <h2 className="mb-4 text-xl font-bold">بیشتر بخوانید</h2>
            <ul className="space-y-3">
              {article.related.map((r) => (
                <li key={r.slug}>
                  <Link to={`/maghale/${r.slug}`} className="text-brand-teal underline">
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </article>
      </div>
    </Shell>
  );
}
